import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiX, FiSave, FiUpload, FiFile, FiImage } = FiIcons;

const PlantForm = ({ plant, onClose }) => {
  const { addPlant, updatePlant } = usePVData();
  const [formData, setFormData] = useState({
    name: plant?.name || '',
    location: plant?.location || '',
    identifier: plant?.identifier || '',
    meterNumber: plant?.meterNumber || '',
    capacity: plant?.capacity || '',
    stringCount: plant?.stringCount || '',
    inverterCount: plant?.inverterCount || '',
    documents: plant?.documents || [],
    images: plant?.images || []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.location.trim()) newErrors.location = 'Standort ist erforderlich';
    if (!formData.identifier.trim()) newErrors.identifier = 'Kennung ist erforderlich';
    if (!formData.meterNumber.trim()) newErrors.meterNumber = 'Zählernummer ist erforderlich';
    if (!formData.capacity || parseFloat(formData.capacity) <= 0) {
      newErrors.capacity = 'Gültige Kapazität erforderlich';
    }
    if (!formData.stringCount || parseInt(formData.stringCount) <= 0) {
      newErrors.stringCount = 'Gültige Anzahl Strings erforderlich';
    }
    if (!formData.inverterCount || parseInt(formData.inverterCount) <= 0) {
      newErrors.inverterCount = 'Gültige Anzahl Wechselrichter erforderlich';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (plant) {
        await updatePlant(plant.id, formData);
      } else {
        await addPlant(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error submitting plant form:', error);
      // Show error to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (type, files) => {
    const fileArray = Array.from(files).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      file // Keep the actual file for upload
    }));
    
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ...fileArray]
    }));
  };

  const removeFile = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {plant ? 'Anlage bearbeiten' : 'Neue Anlage hinzufügen'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anlagenname *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. Hauptgebäude Süddach"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standort *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. München, Musterstraße 123"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kennung *
              </label>
              <input
                type="text"
                value={formData.identifier}
                onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.identifier ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. PV-001"
              />
              {errors.identifier && <p className="text-red-500 text-sm mt-1">{errors.identifier}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zählernummer *
              </label>
              <input
                type="text"
                value={formData.meterNumber}
                onChange={(e) => setFormData({ ...formData, meterNumber: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.meterNumber ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. 1234567890"
              />
              {errors.meterNumber && <p className="text-red-500 text-sm mt-1">{errors.meterNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kapazität (kWp) *
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.capacity ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. 9.9"
              />
              {errors.capacity && <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anzahl Strings *
              </label>
              <input
                type="number"
                value={formData.stringCount}
                onChange={(e) => setFormData({ ...formData, stringCount: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.stringCount ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. 2"
              />
              {errors.stringCount && <p className="text-red-500 text-sm mt-1">{errors.stringCount}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anzahl Wechselrichter *
              </label>
              <input
                type="number"
                value={formData.inverterCount}
                onChange={(e) => setFormData({ ...formData, inverterCount: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.inverterCount ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="z.B. 1"
              />
              {errors.inverterCount && <p className="text-red-500 text-sm mt-1">{errors.inverterCount}</p>}
            </div>
          </div>

          {/* Dokumenten-Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PDF Dokumentation
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <SafeIcon icon={FiFile} className="text-gray-400 text-3xl mx-auto mb-2" />
              <p className="text-gray-600 mb-2">PDF-Dateien hier ablegen oder klicken zum Auswählen</p>
              <input
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => handleFileUpload('documents', e.target.files)}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiUpload} />
                <span>Dateien auswählen</span>
              </label>
            </div>
            {formData.documents.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiFile} className="text-red-500" />
                      <span className="text-sm font-medium">{doc.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile('documents', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bilder-Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dachbilder (JPG)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <SafeIcon icon={FiImage} className="text-gray-400 text-3xl mx-auto mb-2" />
              <p className="text-gray-600 mb-2">JPG-Bilder hier ablegen oder klicken zum Auswählen</p>
              <input
                type="file"
                accept=".jpg,.jpeg"
                multiple
                onChange={(e) => handleFileUpload('images', e.target.files)}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiUpload} />
                <span>Bilder auswählen</span>
              </label>
            </div>
            {formData.images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile('images', index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Speichern...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>{plant ? 'Aktualisieren' : 'Hinzufügen'}</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default PlantForm;